import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useGifStore } from './gif-store';
import { calculateGifDuration } from './types';
import {
  Play,
  Pause,
  Download,
  Loader2,
  ImageIcon,
  RefreshCw,
  AlertCircle,
  FileImage,
  Trash2,
} from 'lucide-react';

interface GifPreviewProps {
  className?: string;
}

export function GifPreview({ className }: GifPreviewProps) {
  const frames = useGifStore((state) => state.frames);
  const settings = useGifStore((state) => state.settings);
  const isEncoding = useGifStore((state) => state.isEncoding);
  const encodingProgress = useGifStore((state) => state.encodingProgress);
  const previewUrl = useGifStore((state) => state.previewUrl);
  const outputBlob = useGifStore((state) => state.outputBlob);
  const error = useGifStore((state) => state.error);
  const startEncoding = useGifStore((state) => state.startEncoding);
  const downloadGif = useGifStore((state) => state.downloadGif);
  const clearOutput = useGifStore((state) => state.clearOutput);
  const setError = useGifStore((state) => state.setError);

  const [isPlaying, setIsPlaying] = useState(true);
  const [previewFrameIndex, setPreviewFrameIndex] = useState(0);
  const [bounceDirection, setBounceDirection] = useState<'forward' | 'backward'>('forward');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const sortedFrames = [...frames].sort((a, b) => a.order - b.order);
  const estimatedDuration = calculateGifDuration(frames, settings);

  // Simple frame animation preview (before encoding)
  useEffect(() => {
    if (previewUrl || isEncoding || sortedFrames.length === 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setPreviewFrameIndex((prev) => {
          const maxIndex = sortedFrames.length - 1;
          
          switch (settings.playbackMode) {
            case 'one-shot':
              // Play once and stop at the last frame
              if (prev >= maxIndex) {
                setIsPlaying(false);
                return maxIndex;
              }
              return prev + 1;
              
            case 'bounce':
              // Ping-pong: forward then backward
              if (bounceDirection === 'forward') {
                if (prev >= maxIndex) {
                  setBounceDirection('backward');
                  return maxIndex - 1;
                }
                return prev + 1;
              } else {
                if (prev <= 0) {
                  setBounceDirection('forward');
                  return 1;
                }
                return prev - 1;
              }
              
            case 'loop':
            default:
              // Simple loop
              return (prev + 1) % sortedFrames.length;
          }
        });
      }, settings.defaultFrameDuration);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, sortedFrames.length, settings.defaultFrameDuration, settings.playbackMode, bounceDirection, previewUrl, isEncoding]);

  // Reset preview state when playback mode changes
  useEffect(() => {
    setPreviewFrameIndex(0);
    setBounceDirection('forward');
    setIsPlaying(true);
  }, [settings.playbackMode]);

  const handleGenerate = () => {
    setError(null);
    startEncoding();
  };

  const handleDownload = () => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    downloadGif(`animation-${timestamp}.gif`);
  };

  const handleClearGif = () => {
    clearOutput();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2">
          <FileImage className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Preview</CardTitle>
        </div>
        <CardDescription>
          Preview your animation and generate the GIF
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Preview Area */}
        <div className="relative aspect-video rounded-lg border-2 border-dashed border-muted bg-muted/30 overflow-hidden">
          {frames.length === 0 ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
              <ImageIcon className="h-12 w-12 mb-2 opacity-40" />
              <p className="text-sm">Add frames to preview</p>
            </div>
          ) : previewUrl ? (
            // Show the actual encoded GIF
            <img
              src={previewUrl}
              alt="Generated GIF"
              className="w-full h-full object-contain"
            />
          ) : (
            // Show frame-by-frame animation preview
            <>
              <img
                src={sortedFrames[previewFrameIndex]?.imageData}
                alt={`Preview frame ${previewFrameIndex + 1}`}
                className="w-full h-full object-contain"
              />
              {/* Frame counter */}
              <Badge
                variant="secondary"
                className="absolute bottom-2 left-2 font-mono"
              >
                {previewFrameIndex + 1} / {sortedFrames.length}
              </Badge>
              {/* Play/Pause overlay button */}
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="absolute top-2 right-2 p-2 rounded-full bg-background/80 hover:bg-background text-foreground transition-colors"
                aria-label={isPlaying ? 'Pause preview' : 'Play preview'}
              >
                {isPlaying ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
              </button>
            </>
          )}

          {/* Encoding overlay */}
          {isEncoding && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/80">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
              <p className="text-sm font-medium">Generating GIF...</p>
              <Progress value={encodingProgress} className="w-2/3 mt-3" />
              <p className="text-xs text-muted-foreground mt-2">
                {encodingProgress}% complete
              </p>
            </div>
          )}
        </div>

        {/* Error display */}
        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Stats */}
        {frames.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div className="p-2 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground">Frames</p>
              <p className="text-lg font-semibold">{frames.length}</p>
            </div>
            <div className="p-2 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground">Duration</p>
              <p className="text-lg font-semibold">{formatDuration(estimatedDuration)}</p>
            </div>
            <div className="p-2 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground">FPS</p>
              <p className="text-lg font-semibold">{settings.frameRate}</p>
            </div>
            <div className="p-2 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground">Size</p>
              <p className="text-lg font-semibold">
                {outputBlob ? formatFileSize(outputBlob.size) : ', '}
              </p>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={handleGenerate}
            disabled={frames.length === 0 || isEncoding}
            className="flex-1"
          >
            {isEncoding ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : previewUrl ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Regenerate GIF
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Generate GIF
              </>
            )}
          </Button>

          {outputBlob && (
            <>
              <Button
                variant="secondary"
                onClick={handleDownload}
                disabled={isEncoding}
                className="flex-1 sm:flex-none"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              <Button
                variant="outline"
                onClick={handleClearGif}
                disabled={isEncoding}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">Delete GIF</span>
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default GifPreview;
